import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  getMidpointMediaNotificationTemplate,
  previewMidpointMediaNotificationTemplate,
  resetMidpointMediaNotificationTemplate,
  updateMidpointMediaNotificationTemplate,
} from "../../api/midpointMediaAdminApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { MIDPOINT_MEDIA_ADMIN_PORTAL_BASE } from "../../constants/clientAdminPortalRoutes";
import { useClientPortalBase, useClientPortalPaths } from "../../hooks/useClientPortalBase";

function applyTags(template, vars = {}) {
  return String(template ?? "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
    const value = vars[key];
    return value == null ? `{{${key}}}` : String(value);
  });
}

function insertAtCursor(fieldRef, value, setter) {
  const el = fieldRef.current;
  if (!el) {
    setter((prev) => `${prev || ""}${value}`);
    return;
  }
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  const next = `${el.value.slice(0, start)}${value}${el.value.slice(end)}`;
  setter(next);
  requestAnimationFrame(() => {
    el.focus();
    const pos = start + value.length;
    el.setSelectionRange(pos, pos);
  });
}

export default function ClientSettingsNotificationEditorPage() {
  const { clientViewId, templateKey } = useParams();
  const portalBase = useClientPortalBase();
  const paths = useClientPortalPaths();
  const isMidpointMedia = portalBase === MIDPOINT_MEDIA_ADMIN_PORTAL_BASE;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [template, setTemplate] = useState(null);
  const [form, setForm] = useState({
    enabled: true,
    subject: "",
    title: "",
    previewText: "",
    bodyText: "",
    ctaLabel: "",
    footerNote: "",
  });
  const [activeField, setActiveField] = useState("bodyText");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const subjectRef = useRef(null);
  const titleRef = useRef(null);
  const previewRef = useRef(null);
  const bodyRef = useRef(null);
  const ctaRef = useRef(null);
  const footerRef = useRef(null);

  const listHref = paths.viewSection(clientViewId, "settings", "notifications");

  const fieldRefs = useMemo(
    () => ({
      subject: subjectRef,
      title: titleRef,
      previewText: previewRef,
      bodyText: bodyRef,
      ctaLabel: ctaRef,
      footerNote: footerRef,
    }),
    []
  );

  const load = useCallback(async () => {
    if (!clientViewId || !templateKey) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getMidpointMediaNotificationTemplate(clientViewId, templateKey);
      const next = body.template;
      setTemplate(next);
      setForm({
        enabled: next.enabled !== false,
        subject: next.subject || "",
        title: next.title || "",
        previewText: next.previewText || "",
        bodyText: next.bodyText || "",
        ctaLabel: next.ctaLabel || "",
        footerNote: next.footerNote || "",
      });
    } catch (error) {
      setErrorMessage(error.message || "Unable to load notification.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, templateKey]);

  useEffect(() => {
    load();
  }, [load]);

  const exampleVars = useMemo(() => {
    const vars = {};
    (template?.tags || []).forEach((tag) => {
      vars[tag.name] = tag.example;
    });
    return vars;
  }, [template]);

  useEffect(() => {
    if (!clientViewId || !templateKey || loading) return undefined;
    const handle = setTimeout(async () => {
      try {
        const body = await previewMidpointMediaNotificationTemplate(clientViewId, templateKey, form);
        setPreviewHtml(body.preview?.html || "");
        setPreviewSubject(body.preview?.subject || applyTags(form.subject, exampleVars));
      } catch {
        setPreviewSubject(applyTags(form.subject, exampleVars));
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [clientViewId, templateKey, form, exampleVars, loading]);

  const insertTag = (tagName) => {
    const token = `{{${tagName}}}`;
    const field = fieldRefs[activeField] ? activeField : "bodyText";
    insertAtCursor(fieldRefs[field], token, (value) => {
      setForm((prev) => ({ ...prev, [field]: typeof value === "function" ? value(prev[field]) : value }));
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const body = await updateMidpointMediaNotificationTemplate(clientViewId, templateKey, form);
      setTemplate(body.template);
      setSuccessMessage("Notification saved.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to save notification.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Restore the default copy for this notification?")) return;
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const body = await resetMidpointMediaNotificationTemplate(clientViewId, templateKey);
      const next = body.template;
      setTemplate(next);
      setForm({
        enabled: next.enabled !== false,
        subject: next.subject || "",
        title: next.title || "",
        previewText: next.previewText || "",
        bodyText: next.bodyText || "",
        ctaLabel: next.ctaLabel || "",
        footerNote: next.footerNote || "",
      });
      setSuccessMessage("Restored the default copy.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to restore the default copy.");
    } finally {
      setSaving(false);
    }
  };

  if (!isMidpointMedia) {
    return <Navigate to={paths.viewSection(clientViewId, "settings")} replace />;
  }

  if (loading) {
    return <ClientPortalLoading message="Loading notification…" />;
  }

  if (!template) {
    return (
      <>
        <ClientPortalError message={errorMessage || "Notification not found."} />
        <p>
          <Link to={listHref}>Back to notifications</Link>
        </p>
      </>
    );
  }

  return (
    <>
      <section className="panel-card cp-settings-section-header">
        <p className="eyebrow">
          <Link to={listHref}>Notifications</Link>
        </p>
        <h3>{template.name}</h3>
        <p className="muted">{template.description}</p>
        <p className="muted">
          {template.category} · Sent to {template.audience.toLowerCase()} from {template.source}.
          {template.customized ? " This copy has been customized." : " Using the default copy."}
        </p>
      </section>

      <ClientPortalError message={errorMessage} />
      {successMessage ? <p className="admin-users-success panel-card">{successMessage}</p> : null}

      <div className="cp-notify-editor">
        <form className="panel-card cp-notify-editor__form" onSubmit={handleSave}>
          <label className="cp-settings-checkbox">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))}
            />
            Send this notification
          </label>

          <div className="cp-notify-tags">
            <p className="cp-notify-tags__label">Insert a tag into the selected field</p>
            <div className="cp-notify-tags__list">
              {(template.tags || []).map((tag) => (
                <button
                  key={tag.name}
                  type="button"
                  className="cp-notify-tag"
                  title={`${tag.label}. Example: ${tag.example}`}
                  onClick={() => insertTag(tag.name)}
                >
                  <code>{`{{${tag.name}}}`}</code>
                  <span>{tag.label}</span>
                </button>
              ))}
            </div>
            <p className="muted cp-notify-tags__hint">
              Tags are replaced with live values when the email is sent. The button label link uses{" "}
              <code>{`{{${template.ctaUrlTag || "url"}}}`}</code>.
            </p>
          </div>

          <label className="admin-users-field">
            <span>Subject</span>
            <input
              ref={subjectRef}
              value={form.subject}
              onFocus={() => setActiveField("subject")}
              onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
              required
            />
          </label>
          <label className="admin-users-field">
            <span>Title</span>
            <input
              ref={titleRef}
              value={form.title}
              onFocus={() => setActiveField("title")}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
            />
          </label>
          <label className="admin-users-field">
            <span>Preview text</span>
            <input
              ref={previewRef}
              value={form.previewText}
              onFocus={() => setActiveField("previewText")}
              onChange={(event) => setForm((prev) => ({ ...prev, previewText: event.target.value }))}
            />
          </label>
          <label className="admin-users-field">
            <span>Body</span>
            <textarea
              ref={bodyRef}
              rows={12}
              value={form.bodyText}
              onFocus={() => setActiveField("bodyText")}
              onChange={(event) => setForm((prev) => ({ ...prev, bodyText: event.target.value }))}
              required
            />
          </label>
          <label className="admin-users-field">
            <span>Button label</span>
            <input
              ref={ctaRef}
              value={form.ctaLabel}
              onFocus={() => setActiveField("ctaLabel")}
              onChange={(event) => setForm((prev) => ({ ...prev, ctaLabel: event.target.value }))}
            />
          </label>
          <label className="admin-users-field">
            <span>Footer note</span>
            <textarea
              ref={footerRef}
              rows={3}
              value={form.footerNote}
              onFocus={() => setActiveField("footerNote")}
              onChange={(event) => setForm((prev) => ({ ...prev, footerNote: event.target.value }))}
            />
          </label>

          <div className="cp-notify-editor__actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save notification"}
            </button>
            <button type="button" className="btn-sm" onClick={handleReset} disabled={saving}>
              Restore default
            </button>
          </div>
        </form>

        <aside className="panel-card cp-notify-preview">
          <h4>Email preview</h4>
          <p className="cp-notify-preview__subject">{previewSubject || applyTags(form.subject, exampleVars)}</p>
          {previewHtml ? (
            <iframe
              className="cp-notify-preview-frame"
              title="Branded email preview"
              sandbox="allow-same-origin"
              srcDoc={previewHtml}
            />
          ) : (
            <p className="muted">Loading branded preview…</p>
          )}
        </aside>
      </div>
    </>
  );
}
